import { useReadPresetApi } from '@aibees/shared/api';
import {
  OptionType,
  TemplateCategory,
  TemplateOption,
} from '@aibees/shared/features';
import { useMemo } from 'react';
import { DEFAULT_EXPORT_TEMPLATE_ID } from '../constants';
import { useTemplateContext } from '../context/TemplateContext';
import { TransformedData } from '../types';
import { useDefaultTemplateData } from './use-default-template-data';
import { useSanitizeExportSetting } from './use-sanitize-export-setting';

const isStoredDefault = (
  value: unknown
): value is { default: true; items?: TemplateCategory[] } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  (value as { default?: boolean }).default === true;

export function useConvertDataToExportSetting() {
  const { selectedTemplate, selectedPreset } = useTemplateContext();
  const defaultTemplateData = useDefaultTemplateData();
  const sanitizeExportSetting = useSanitizeExportSetting();

  // Resolve the exact template being exported by id. Reading by id (instead of
  // browsing by name and matching) avoids ambiguity across duplicate-named
  // presets and shares its React Query cache with the edit dialog, so the
  // edit/export flow issues a single request.
  const selectedId = selectedTemplate?.id ?? selectedPreset?.value;

  // The default template is synthesized on the frontend, so there is no backend
  // preset to read — fall back to the in-code default columns.
  const isDefault = !selectedId || selectedId === DEFAULT_EXPORT_TEMPLATE_ID;

  const { data: preset } = useReadPresetApi<TemplateCategory[]>(selectedId, {
    enabled: !isDefault,
  });

  const items: TemplateCategory[] | undefined = useMemo(() => {
    if (isDefault) return undefined;
    // A legacy persisted default preset may still be referenced; ignore its
    // stale snapshot so defaultTemplateData (the in-code constant) is used.
    if (isStoredDefault(preset?.value)) return undefined;
    return preset?.value as TemplateCategory[] | undefined;
  }, [isDefault, preset?.value]);

  const prepareDataForServer = useMemo(() => {
    return items?.reduce<TransformedData>((acc, item) => {
      if (item.isSingleEntry && item.entries[0]) {
        const fields = item.entries[0].fields as unknown as OptionType[];
        acc[item.key] = { fields: fields as unknown as TemplateOption[] };
      } else if (!item.isSingleEntry) {
        acc[item.key] = item.entries.map((entry, index) => {
          const fields = entry.fields as unknown as OptionType[];
          return {
            index: entry.index ?? index,
            fields: fields as unknown as TemplateOption[],
          };
        });
      }
      return acc;
    }, {});
  }, [items]);
  const setting =
    prepareDataForServer && Object.keys(prepareDataForServer).length > 0
      ? prepareDataForServer
      : defaultTemplateData;

  return useMemo(
    () =>
      setting
        ? sanitizeExportSetting(setting as unknown as TransformedData)
        : setting,
    [setting, sanitizeExportSetting]
  );
}
